import { ErrorMessage } from '@hookform/error-message';
import React, { ReactNode } from 'react';
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FieldValues,
  Path,
  RegisterOptions,
  UseFormReturn,
  UseFormStateReturn,
} from 'react-hook-form';

export type InputProps = React.DetailedHTMLProps<
  React.InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
>;

export type InputGroupProps<T extends FieldValues, TName extends Path<T>> = {
  name?: TName;
  form?: UseFormReturn<T>;
  error?: string;
  register?: RegisterOptions<T>;
  row?: boolean;
  className?: string;
  inputClass?: string;
  errorClass?: string;
  label?: string | ReactNode;
  labelClass?: string;
  required?: boolean;
  inputWrapClass?: string;
  inputProps?: Omit<InputProps, 'form' | 'name'>;
  renderInput?(props: InputProps): ReactNode;
  renderController?: ({
    field,
    fieldState,
    formState,
  }: {
    field: ControllerRenderProps<T, TName>;
    fieldState: ControllerFieldState;
    formState: UseFormStateReturn<T>;
  }) => React.ReactElement;
};

const defaultClasses = {
  inputClass: 'w-full px-5 py-4 rounded-lg border border-gray-d9 leading-4',
  errorClass: 'text-danger text-sm p-2',
  labelClass: 'text-base font-bold text-secondary',
};

const rowClasses = {
  className: 'flex flex-wrap items-center gap-x-4 gap-y-2',
  inputWrapClass: 'flex-grow',
};
const colClasses = {
  className: 'text-start w-full',
};

export const InputGroup = Object.assign(InputGroupComponent, defaultClasses, rowClasses);

function InputGroupComponent<T extends FieldValues, TName extends Path<T>>({
  row,
  className = row ? rowClasses.className : colClasses.className,
  inputClass = defaultClasses.inputClass,
  errorClass = defaultClasses.errorClass,
  inputWrapClass = row ? rowClasses.inputWrapClass : undefined,
  label,
  labelClass = row ? defaultClasses.labelClass : `${defaultClasses.labelClass} mb-3`,
  required,
  form,
  error,
  name,
  register,
  inputProps,
  renderInput,
  renderController,
}: InputGroupProps<T, TName>) {
  const render = () => {
    if (renderController) {
      if (!form) throw new Error('No form but renderController is set.');
      if (!name) throw new Error('No name but renderController is set.');
      return <Controller control={form.control} name={name} render={renderController} />;
    }

    const propsForInput = {
      name,
      ...inputProps,
      ...(name ? form?.register(name, register) : undefined),
      className: inputClass,
    };
    return renderInput ? renderInput(propsForInput) : <input {...propsForInput} />;
  };

  return (
    <div className={className}>
      {label && (
        <div className={labelClass}>
          {required && <span className="text-danger pr-2">*</span>}
          {label}
        </div>
      )}
      <div className={inputWrapClass}>
        {render()}
        {form ? (
          <ErrorMessage
            errors={form.formState.errors}
            name={name as any}
            render={(error: { message: string }) => (
              <div className={errorClass}>{error.message}</div>
            )}
          />
        ) : (
          error && <div className="text-danger text-sm p-2">{error}</div>
        )}
      </div>
    </div>
  );
}
